import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Badge } from './ui/badge';
import { Textarea } from './ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { 
  Shield, 
  Eye, 
  Lock, 
  KeyRound,
  Users, 
  Settings, 
  FileText,
  BarChart3,
  Volume2,
  MapPin,
  Heart,
  BookOpen,
  Coins,
  AlertTriangle,
  CheckCircle,
  Clock,
  ArrowLeft,
  Fingerprint,
  Skull,
  Sparkles,
  ScrollText,
  Activity,
  Database,
  Terminal,
  Layers,
  LogOut
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Niveaux de sécurité - Couches concentriques
const SECURITY_LEVELS = {
  LEVEL_1: { name: 'Observation', color: 'emerald', icon: Eye, description: 'Lecture seule, statistiques' },
  LEVEL_2: { name: 'Gestion', color: 'blue', icon: Settings, description: 'Gestion utilisateurs et contenus' },
  LEVEL_3: { name: 'Configuration', color: 'amber', icon: KeyRound, description: 'Paramètres système' },
  LEVEL_4: { name: 'Coffre Fort', color: 'purple', icon: Coins, description: 'Finances et Charte' },
  LEVEL_5: { name: 'Graine Sacrée', color: 'red', icon: Skull, description: 'Accès total - Triple authentification' }
};

// Raisons prédéfinies pour les actions admin
const ADMIN_INTENTIONS = [
  { id: 'verification', label: 'Vérification de routine', level: 1 },
  { id: 'user_support', label: 'Support utilisateur', level: 2 },
  { id: 'content_moderation', label: 'Modération de contenu', level: 2 },
  { id: 'config_update', label: 'Mise à jour configuration', level: 3 },
  { id: 'financial_audit', label: 'Audit financier', level: 4 },
  { id: 'security_check', label: 'Contrôle de sécurité', level: 4 },
  { id: 'emergency_access', label: 'Accès d\'urgence', level: 5 },
  { id: 'system_maintenance', label: 'Maintenance système', level: 5 },
  { id: 'other', label: 'Autre (préciser)', level: 3 }
];

// Modules accessibles depuis le Centre de Contrôle
const ADMIN_MODULES = [
  { 
    id: 'users', 
    name: 'Utilisateurs', 
    description: 'Gestion des comptes et rôles',
    icon: Users, 
    route: '/admin/clients',
    level: 2,
    color: 'blue'
  },
  { 
    id: 'acoustics', 
    name: 'Acoustique', 
    description: 'Module professionnel',
    icon: Volume2, 
    route: '/acoustics',
    level: 1,
    color: 'cyan'
  },
  { 
    id: 'tourism', 
    name: 'Tourisme', 
    description: 'Carte des Shamans',
    icon: MapPin, 
    route: '/tourism',
    level: 1,
    color: 'emerald'
  },
  { 
    id: 'quotes', 
    name: 'Devis', 
    description: 'Création et gestion',
    icon: FileText, 
    route: '/admin/quotes/create',
    level: 2,
    color: 'purple'
  },
  { 
    id: 'prestations', 
    name: 'Prestations', 
    description: 'Bibliothèque de services',
    icon: Layers, 
    route: '/admin/prestations',
    level: 2,
    color: 'orange'
  },
  { 
    id: 'coffre_fort', 
    name: 'Coffre Fort', 
    description: 'Finances transparentes',
    icon: Coins, 
    route: '/coffre-fort',
    level: 4,
    color: 'amber'
  },
  { 
    id: 'audit_logs', 
    name: 'Journal d\'Audit', 
    description: 'Traçabilité complète',
    icon: ScrollText, 
    route: null, // Intégré dans ce composant
    level: 3,
    color: 'slate'
  },
  { 
    id: 'access_codes', 
    name: 'Codes d\'Accès', 
    description: 'Invitations et promos',
    icon: KeyRound, 
    route: null, // Intégré dans ce composant
    level: 3,
    color: 'violet'
  },
  { 
    id: 'security', 
    name: 'Sécurité', 
    description: 'Mot de passe et 2FA',
    icon: Lock, 
    route: null, // Panneau intégré
    level: 4,
    color: 'red'
  },
  { 
    id: 'graine', 
    name: 'La Graine', 
    description: 'Configuration sacrée',
    icon: Sparkles, 
    route: null, // Accès spécial
    level: 5,
    color: 'red'
  }
];

const AdminControlCenter = ({ user, onLogout }) => {
  const navigate = useNavigate();
  const [currentLevel, setCurrentLevel] = useState(1);
  const [selectedModule, setSelectedModule] = useState(null);
  const [showIntentionModal, setShowIntentionModal] = useState(false);
  const [pendingAction, setPendingAction] = useState(null);
  const [auditLogs, setAuditLogs] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  
  // État pour le panneau de sécurité
  const [showSecurityPanel, setShowSecurityPanel] = useState(false);
  const [securityTab, setSecurityTab] = useState('password'); // 'password' ou '2fa'
  const [passwordForm, setPasswordForm] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });
  const [twoFASetup, setTwoFASetup] = useState({
    secret: '',
    qrUri: '',
    verificationCode: '',
    isEnabled: false
  });
  
  // État pour la déclaration d'intention
  const [intention, setIntention] = useState({
    reason: '',
    customReason: '',
    secretCode: ''
  });
  
  // État pour l'authentification niveau 5
  const [level5Auth, setLevel5Auth] = useState({
    show: false,
    password: '',
    secretCode: '',
    fingerprint: '' // Code unique de session
  });

  useEffect(() => {
    loadDashboardData();
    loadAuditLogs();
  }, []);

  const loadDashboardData = async () => {
    try {
      const [dashboardRes, statsRes] = await Promise.all([
        axios.get(`${API}/admin/dashboard`),
        axios.get(`${API}/strongbox/stats`).catch(() => ({ data: null }))
      ]);
      setStats({
        ...dashboardRes.data,
        strongbox: statsRes.data
      });
    } catch (error) {
      console.error('Error loading dashboard:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadAuditLogs = async () => {
    try {
      const response = await axios.get(`${API}/admin/audit-logs?limit=20`);
      setAuditLogs(response.data || []);
    } catch (error) {
      // Audit logs endpoint may not exist yet
      setAuditLogs([]);
    }
  };

  // ===== FONCTIONS DE SÉCURITÉ =====
  
  // Changer le mot de passe
  const handleChangePassword = async () => {
    if (passwordForm.newPassword !== passwordForm.confirmPassword) {
      toast.error('Les mots de passe ne correspondent pas');
      return;
    }
    if (passwordForm.newPassword.length < 8) {
      toast.error('Le mot de passe doit contenir au moins 8 caractères');
      return;
    }
    
    try {
      await axios.post(`${API}/admin/change-password`, {
        current_password: passwordForm.currentPassword,
        new_password: passwordForm.newPassword,
        confirm_password: passwordForm.confirmPassword
      });
      toast.success('🔐 Mot de passe modifié ! Reconnexion nécessaire...');
      setPasswordForm({ currentPassword: '', newPassword: '', confirmPassword: '' });
      setShowSecurityPanel(false);
      
      // Forcer la déconnexion pour utiliser le nouveau mot de passe
      setTimeout(() => {
        onLogout();
        navigate('/login');
      }, 2000);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors du changement de mot de passe');
    }
  };
  
  // Configurer le 2FA
  const handleSetup2FA = async () => {
    try {
      const response = await axios.post(`${API}/admin/setup-2fa`);
      setTwoFASetup({
        ...twoFASetup,
        secret: response.data.secret,
        qrUri: response.data.qr_uri
      });
      toast.info('Scannez le QR code avec votre application d\'authentification');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la configuration 2FA');
    }
  };
  
  // Vérifier et activer le 2FA
  const handleVerify2FA = async () => {
    if (!twoFASetup.verificationCode) {
      toast.error('Entrez le code de vérification');
      return;
    }
    
    try {
      await axios.post(`${API}/admin/verify-2fa`, {
        totp_code: twoFASetup.verificationCode
      });
      toast.success('🔐 2FA activé ! Reconnexion nécessaire...');
      setTwoFASetup({ ...twoFASetup, isEnabled: true, verificationCode: '' });
      
      // Forcer la déconnexion pour tester le 2FA
      setTimeout(() => {
        onLogout();
        navigate('/login');
      }, 2000);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Code incorrect');
    }
  };
  
  // Ouvrir le panneau de sécurité
  const openSecurityPanel = () => {
    setShowSecurityPanel(true);
    setSecurityTab('password');
  };

  // Journaliser une action admin
  const logAdminAction = async (action, moduleId, details = {}) => {
    try {
      await axios.post(`${API}/admin/audit-log`, {
        action,
        module: moduleId,
        intention: intention.reason,
        custom_reason: intention.customReason,
        details,
        timestamp: new Date().toISOString()
      });
    } catch (error) {
      console.error('Failed to log action:', error);
    }
  };

  // Gérer le clic sur un module
  const handleModuleClick = (module) => {
    if (module.level > currentLevel) {
      toast.error(`Niveau de sécurité insuffisant. Requis: ${SECURITY_LEVELS[`LEVEL_${module.level}`].name}`);
      return;
    }
    
    // Module sécurité - ouvrir le panneau directement
    if (module.id === 'security') {
      openSecurityPanel();
      return;
    }
    
    // Si niveau >= 3, demander une déclaration d'intention
    if (module.level >= 3) {
      setPendingAction(module);
      setShowIntentionModal(true);
    } else {
      // Accès direct pour niveaux 1-2
      executeModuleAccess(module);
    }
  };

  // Exécuter l'accès au module après validation
  const executeModuleAccess = async (module) => {
    await logAdminAction('MODULE_ACCESS', module.id, { level: module.level });
    
    if (module.route) {
      navigate(module.route);
    } else {
      setSelectedModule(module.id);
    }
    
    setShowIntentionModal(false);
    setIntention({ reason: '', customReason: '', secretCode: '' });
  };

  // Valider l'intention et accéder au module
  const validateIntention = async () => {
    if (!intention.reason) {
      toast.error('Veuillez sélectionner une raison');
      return;
    }
    
    if (intention.reason === 'other' && !intention.customReason) {
      toast.error('Veuillez préciser votre intention');
      return;
    }
    
    // Pour niveau 4+, vérifier le code secret
    if (pendingAction.level >= 4 && !intention.secretCode) {
      toast.error('Code de validation requis');
      return;
    }
    
    // Pour niveau 5, ouvrir le modal de triple authentification
    if (pendingAction.level >= 5) {
      setLevel5Auth({ ...level5Auth, show: true });
      return;
    }
    
    // Exécuter l'accès
    executeModuleAccess(pendingAction);
    toast.success(`Accès accordé: ${pendingAction.name}`);
  };

  // Triple authentification niveau 5
  const validateLevel5Access = async () => {
    // Vérification du mot de passe
    if (!level5Auth.password) {
      toast.error('Mot de passe requis');
      return;
    }
    
    // Vérification du code secret (code maître)
    if (!level5Auth.secretCode) {
      toast.error('Code secret maître requis');
      return;
    }
    
    // Vérification de l'empreinte de session
    if (!level5Auth.fingerprint) {
      toast.error('Code de session requis');
      return;
    }
    
    try {
      // Vérifier auprès du backend
      const response = await axios.post(`${API}/admin/verify-level5`, {
        password: level5Auth.password,
        secret_code: level5Auth.secretCode,
        session_fingerprint: level5Auth.fingerprint
      });
      
      if (response.data.verified) {
        setCurrentLevel(5);
        executeModuleAccess(pendingAction);
        toast.success('🔓 Accès Graine Sacrée accordé');
      } else {
        toast.error('Authentification échouée');
      }
    } catch (error) {
      toast.error('Authentification échouée');
    }
    
    setLevel5Auth({ show: false, password: '', secretCode: '', fingerprint: '' });
  };

  // Élever le niveau de sécurité
  const elevateSecurityLevel = (targetLevel) => {
    if (targetLevel === 5) {
      setLevel5Auth({ ...level5Auth, show: true });
      setPendingAction({ id: 'level_elevation', name: 'Élévation niveau 5', level: 5 });
    } else {
      setCurrentLevel(targetLevel);
      toast.success(`Niveau de sécurité élevé: ${SECURITY_LEVELS[`LEVEL_${targetLevel}`].name}`);
    }
  };

  const getLevelColor = (level) => {
    const colors = {
      1: 'bg-emerald-500',
      2: 'bg-blue-500',
      3: 'bg-amber-500',
      4: 'bg-purple-500',
      5: 'bg-red-500'
    };
    return colors[level] || 'bg-slate-500';
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-900 flex items-center justify-center">
        <div className="text-center">
          <Shield className="w-16 h-16 text-cyan-400 mx-auto mb-4 animate-pulse" />
          <p className="text-cyan-300">Chargement du Centre de Contrôle...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900">
      {/* Header */}
      <div className="bg-slate-900/80 border-b border-cyan-500/30 backdrop-blur-sm sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center space-x-4">
              <Button 
                variant="ghost" 
                size="sm"
                onClick={() => navigate('/')}
                className="text-cyan-400 hover:text-cyan-300"
              >
                <ArrowLeft className="w-4 h-4 mr-2" />
                Retour
              </Button>
              <div className="flex items-center space-x-3">
                <Shield className="w-8 h-8 text-cyan-400" />
                <div>
                  <h1 className="text-xl font-bold text-white">Centre de Contrôle</h1>
                  <p className="text-xs text-cyan-300">Gardien de la Graine</p>
                </div>
              </div>
            </div>
            
            <div className="flex items-center space-x-4">
              {/* Indicateur de niveau de sécurité actuel */}
              <div className="flex items-center space-x-2">
                <span className="text-xs text-slate-400">Niveau:</span>
                <Badge className={`${getLevelColor(currentLevel)} text-white`}>
                  {SECURITY_LEVELS[`LEVEL_${currentLevel}`]?.name}
                </Badge>
              </div>
              
              {/* Info Admin */}
              <Badge variant="outline" className="border-cyan-500/50 text-cyan-300">
                <Fingerprint className="w-3 h-3 mr-1" />
                {user?.username}
              </Badge>
              
              <Button 
                variant="outline" 
                size="sm"
                onClick={onLogout}
                className="border-red-500/50 text-red-400 hover:bg-red-500/10"
              >
                <LogOut className="w-4 h-4 mr-2" />
                Déconnexion
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* Contenu principal */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Statistiques rapides */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-8">
          <Card className="bg-slate-800/50 border-cyan-500/30">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs text-slate-400">Utilisateurs</p>
                  <p className="text-2xl font-bold text-white">
                    {stats?.platform_stats?.total_clients || 0}
                  </p>
                </div>
                <Users className="w-8 h-8 text-cyan-400" />
              </div>
            </CardContent>
          </Card>
          
          <Card className="bg-slate-800/50 border-emerald-500/30">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs text-slate-400">Abonnements</p>
                  <p className="text-2xl font-bold text-white">
                    {stats?.platform_stats?.active_subscriptions || 0}
                  </p>
                </div>
                <CheckCircle className="w-8 h-8 text-emerald-400" />
              </div>
            </CardContent>
          </Card>
          
          <Card className="bg-slate-800/50 border-amber-500/30">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs text-slate-400">Coffre Fort</p>
                  <p className="text-2xl font-bold text-white">
                    {stats?.strongbox?.total_amount?.toFixed(2) || '0.00'}€
                  </p>
                </div>
                <Coins className="w-8 h-8 text-amber-400" />
              </div>
            </CardContent>
          </Card>
          
          <Card className="bg-slate-800/50 border-purple-500/30">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs text-slate-400">Actions (24h)</p>
                  <p className="text-2xl font-bold text-white">
                    {auditLogs.length}
                  </p>
                </div>
                <Activity className="w-8 h-8 text-purple-400" />
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Niveaux de sécurité */}
        <Card className="bg-slate-800/50 border-slate-700 mb-8">
          <CardHeader>
            <CardTitle className="text-white flex items-center">
              <Lock className="w-5 h-5 mr-2 text-cyan-400" />
              Niveaux de Sécurité - Couches Concentriques
            </CardTitle>
            <CardDescription className="text-slate-400">
              Plus vous vous approchez de la Graine, plus la sécurité est renforcée
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="flex flex-wrap gap-2">
              {Object.entries(SECURITY_LEVELS).map(([key, level], index) => {
                const levelNum = index + 1;
                const isActive = currentLevel >= levelNum;
                const LevelIcon = level.icon;
                
                return (
                  <Button
                    key={key}
                    variant={isActive ? "default" : "outline"}
                    size="sm"
                    className={`
                      ${isActive ? getLevelColor(levelNum) : 'bg-slate-700/50'} 
                      ${isActive ? 'text-white' : 'text-slate-400'}
                      border-slate-600
                    `}
                    onClick={() => !isActive && elevateSecurityLevel(levelNum)}
                    disabled={isActive}
                  >
                    <LevelIcon className="w-4 h-4 mr-2" />
                    {level.name}
                    {levelNum === 5 && <Skull className="w-3 h-3 ml-2" />}
                  </Button>
                );
              })}
            </div>
          </CardContent>
        </Card>

        {/* Grille des modules */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
          {ADMIN_MODULES.map((module) => {
            const ModuleIcon = module.icon;
            const isAccessible = currentLevel >= module.level;
            const levelInfo = SECURITY_LEVELS[`LEVEL_${module.level}`];
            
            return (
              <motion.div
                key={module.id}
                whileHover={isAccessible ? { scale: 1.02 } : {}}
                whileTap={isAccessible ? { scale: 0.98 } : {}}
              >
                <Card 
                  className={`
                    cursor-pointer transition-all
                    ${isAccessible 
                      ? `bg-slate-800/50 border-${module.color}-500/50 hover:border-${module.color}-400` 
                      : 'bg-slate-900/50 border-slate-700 opacity-50'}
                  `}
                  onClick={() => handleModuleClick(module)}
                >
                  <CardContent className="p-6">
                    <div className="flex items-start justify-between mb-4">
                      <div className={`p-3 rounded-lg bg-${module.color}-500/20`}>
                        <ModuleIcon className={`w-6 h-6 text-${module.color}-400`} />
                      </div>
                      <Badge 
                        variant="outline" 
                        className={`text-xs ${isAccessible ? `border-${module.color}-500/50 text-${module.color}-300` : 'border-slate-600 text-slate-500'}`}
                      >
                        {levelInfo?.name}
                      </Badge>
                    </div>
                    <h3 className={`font-semibold mb-1 ${isAccessible ? 'text-white' : 'text-slate-500'}`}>
                      {module.name}
                    </h3>
                    <p className={`text-sm ${isAccessible ? 'text-slate-400' : 'text-slate-600'}`}>
                      {module.description}
                    </p>
                    {!isAccessible && (
                      <div className="mt-3 flex items-center text-xs text-red-400">
                        <Lock className="w-3 h-3 mr-1" />
                        Niveau {module.level} requis
                      </div>
                    )}
                  </CardContent>
                </Card>
              </motion.div>
            );
          })}
        </div>

        {/* Journal d'audit récent */}
        <Card className="bg-slate-800/50 border-slate-700">
          <CardHeader>
            <CardTitle className="text-white flex items-center">
              <ScrollText className="w-5 h-5 mr-2 text-purple-400" />
              Journal d'Audit - Dernières Actions
            </CardTitle>
          </CardHeader>
          <CardContent>
            {auditLogs.length > 0 ? (
              <div className="space-y-2">
                {auditLogs.slice(0, 10).map((log, index) => (
                  <div key={index} className="flex items-center justify-between p-3 bg-slate-900/50 rounded-lg">
                    <div className="flex items-center space-x-3">
                      <Clock className="w-4 h-4 text-slate-500" />
                      <div>
                        <p className="text-sm text-white">{log.action}</p>
                        <p className="text-xs text-slate-400">{log.intention || 'Non spécifié'}</p>
                      </div>
                    </div>
                    <span className="text-xs text-slate-500">
                      {new Date(log.timestamp).toLocaleString('fr-FR')}
                    </span>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8 text-slate-500">
                <ScrollText className="w-12 h-12 mx-auto mb-4 opacity-50" />
                <p>Aucune action enregistrée</p>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Modal de déclaration d'intention */}
      <AnimatePresence>
        {showIntentionModal && pendingAction && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-center justify-center"
            onClick={() => setShowIntentionModal(false)}
          >
            <motion.div
              initial={{ scale: 0.9, y: 20 }}
              animate={{ scale: 1, y: 0 }}
              exit={{ scale: 0.9, y: 20 }}
              className="bg-slate-900 border border-amber-500/50 rounded-2xl p-8 max-w-md mx-4"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="text-center mb-6">
                <AlertTriangle className="w-12 h-12 text-amber-400 mx-auto mb-4" />
                <h3 className="text-xl font-bold text-white mb-2">Déclaration d'Intention</h3>
                <p className="text-slate-400">
                  Accès au module: <span className="text-amber-400">{pendingAction.name}</span>
                </p>
                <Badge className={`mt-2 ${getLevelColor(pendingAction.level)} text-white`}>
                  Niveau {pendingAction.level} - {SECURITY_LEVELS[`LEVEL_${pendingAction.level}`]?.name}
                </Badge>
              </div>
              
              <div className="space-y-4">
                <div>
                  <Label className="text-slate-300">Raison de l'accès</Label>
                  <Select value={intention.reason} onValueChange={(v) => setIntention({...intention, reason: v})}>
                    <SelectTrigger className="bg-slate-800 border-slate-600 text-white">
                      <SelectValue placeholder="Sélectionner une raison..." />
                    </SelectTrigger>
                    <SelectContent className="bg-slate-800 border-slate-600">
                      {ADMIN_INTENTIONS.filter(i => i.level <= pendingAction.level).map((item) => (
                        <SelectItem key={item.id} value={item.id} className="text-white">
                          {item.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                
                {intention.reason === 'other' && (
                  <div>
                    <Label className="text-slate-300">Précisez votre intention</Label>
                    <Textarea
                      value={intention.customReason}
                      onChange={(e) => setIntention({...intention, customReason: e.target.value})}
                      className="bg-slate-800 border-slate-600 text-white"
                      placeholder="Décrivez précisément pourquoi vous avez besoin de cet accès..."
                    />
                  </div>
                )}
                
                {pendingAction.level >= 4 && (
                  <div>
                    <Label className="text-slate-300">Code de validation</Label>
                    <Input
                      type="password"
                      value={intention.secretCode}
                      onChange={(e) => setIntention({...intention, secretCode: e.target.value})}
                      className="bg-slate-800 border-slate-600 text-white"
                      placeholder="Entrez votre code secret..."
                    />
                  </div>
                )}
                
                <div className="flex space-x-3 pt-4">
                  <Button
                    variant="outline"
                    className="flex-1 border-slate-600 text-slate-300"
                    onClick={() => setShowIntentionModal(false)}
                  >
                    Annuler
                  </Button>
                  <Button
                    className="flex-1 bg-amber-600 hover:bg-amber-500 text-white"
                    onClick={validateIntention}
                  >
                    <Shield className="w-4 h-4 mr-2" />
                    Confirmer
                  </Button>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Modal Triple Authentification Niveau 5 */}
      <AnimatePresence>
        {level5Auth.show && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/80 backdrop-blur-md z-50 flex items-center justify-center"
            onClick={() => setLevel5Auth({...level5Auth, show: false})}
          >
            <motion.div
              initial={{ scale: 0.9, y: 20 }}
              animate={{ scale: 1, y: 0 }}
              exit={{ scale: 0.9, y: 20 }}
              className="bg-slate-900 border-2 border-red-500/50 rounded-2xl p-8 max-w-md mx-4"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="text-center mb-6">
                <Skull className="w-16 h-16 text-red-500 mx-auto mb-4" />
                <h3 className="text-xl font-bold text-red-400 mb-2">⚠️ TRIPLE AUTHENTIFICATION</h3>
                <p className="text-slate-400">
                  Accès à la <span className="text-red-400 font-bold">Graine Sacrée</span>
                </p>
                <p className="text-xs text-slate-500 mt-2">
                  Cette action sera enregistrée et analysée par le Gardien IA
                </p>
              </div>
              
              <div className="space-y-4">
                <div>
                  <Label className="text-slate-300">1. Mot de passe Admin</Label>
                  <Input
                    type="password"
                    value={level5Auth.password}
                    onChange={(e) => setLevel5Auth({...level5Auth, password: e.target.value})}
                    className="bg-slate-800 border-red-500/30 text-white"
                    placeholder="••••••••"
                  />
                </div>
                
                <div>
                  <Label className="text-slate-300">2. Code Secret Maître</Label>
                  <Input
                    type="password"
                    value={level5Auth.secretCode}
                    onChange={(e) => setLevel5Auth({...level5Auth, secretCode: e.target.value})}
                    className="bg-slate-800 border-red-500/30 text-white"
                    placeholder="Code unique de la Graine..."
                  />
                </div>
                
                <div>
                  <Label className="text-slate-300">3. Empreinte de Session</Label>
                  <Input
                    type="text"
                    value={level5Auth.fingerprint}
                    onChange={(e) => setLevel5Auth({...level5Auth, fingerprint: e.target.value})}
                    className="bg-slate-800 border-red-500/30 text-white font-mono"
                    placeholder="TKALE-XXXXX"
                  />
                  <p className="text-xs text-slate-500 mt-1">
                    Généré par le Gardien IA pour cette session uniquement
                  </p>
                </div>
                
                <div className="flex space-x-3 pt-4">
                  <Button
                    variant="outline"
                    className="flex-1 border-slate-600 text-slate-300"
                    onClick={() => setLevel5Auth({...level5Auth, show: false})}
                  >
                    Annuler
                  </Button>
                  <Button
                    className="flex-1 bg-red-600 hover:bg-red-500 text-white"
                    onClick={validateLevel5Access}
                  >
                    <Skull className="w-4 h-4 mr-2" />
                    Accéder à la Graine
                  </Button>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Modal Panneau de Sécurité */}
      <AnimatePresence>
        {showSecurityPanel && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/80 backdrop-blur-md z-50 flex items-center justify-center"
            onClick={() => setShowSecurityPanel(false)}
          >
            <motion.div
              initial={{ scale: 0.9, y: 20 }}
              animate={{ scale: 1, y: 0 }}
              exit={{ scale: 0.9, y: 20 }}
              className="bg-slate-900 border-2 border-red-500/50 rounded-2xl p-8 max-w-lg mx-4 w-full"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="text-center mb-6">
                <Lock className="w-12 h-12 text-red-400 mx-auto mb-4" />
                <h3 className="text-xl font-bold text-white mb-2">🔐 Sécurité Admin</h3>
                <p className="text-slate-400 text-sm">
                  Protégez votre accès à la Graine
                </p>
              </div>
              
              {/* Onglets */}
              <div className="flex space-x-2 mb-6">
                <Button
                  variant={securityTab === 'password' ? 'default' : 'outline'}
                  className={securityTab === 'password' ? 'bg-red-600' : 'border-slate-600 text-slate-300'}
                  onClick={() => setSecurityTab('password')}
                >
                  <KeyRound className="w-4 h-4 mr-2" />
                  Mot de passe
                </Button>
                <Button
                  variant={securityTab === '2fa' ? 'default' : 'outline'}
                  className={securityTab === '2fa' ? 'bg-red-600' : 'border-slate-600 text-slate-300'}
                  onClick={() => setSecurityTab('2fa')}
                >
                  <Fingerprint className="w-4 h-4 mr-2" />
                  2FA
                </Button>
              </div>
              
              {/* Contenu - Changement de mot de passe */}
              {securityTab === 'password' && (
                <div className="space-y-4">
                  <div>
                    <Label className="text-slate-300">Mot de passe actuel</Label>
                    <Input
                      type="password"
                      value={passwordForm.currentPassword}
                      onChange={(e) => setPasswordForm({...passwordForm, currentPassword: e.target.value})}
                      className="bg-slate-800 border-slate-600 text-white"
                      placeholder="••••••••"
                    />
                  </div>
                  <div>
                    <Label className="text-slate-300">Nouveau mot de passe</Label>
                    <Input
                      type="password"
                      value={passwordForm.newPassword}
                      onChange={(e) => setPasswordForm({...passwordForm, newPassword: e.target.value})}
                      className="bg-slate-800 border-slate-600 text-white"
                      placeholder="Minimum 8 caractères"
                    />
                  </div>
                  <div>
                    <Label className="text-slate-300">Confirmer le nouveau mot de passe</Label>
                    <Input
                      type="password"
                      value={passwordForm.confirmPassword}
                      onChange={(e) => setPasswordForm({...passwordForm, confirmPassword: e.target.value})}
                      className="bg-slate-800 border-slate-600 text-white"
                      placeholder="••••••••"
                    />
                  </div>
                  <Button
                    className="w-full bg-red-600 hover:bg-red-500 text-white mt-4"
                    onClick={handleChangePassword}
                  >
                    <Lock className="w-4 h-4 mr-2" />
                    Changer le mot de passe
                  </Button>
                </div>
              )}
              
              {/* Contenu - Configuration 2FA */}
              {securityTab === '2fa' && (
                <div className="space-y-4">
                  {!twoFASetup.secret ? (
                    <div className="text-center">
                      <p className="text-slate-400 mb-4">
                        Activez l'authentification à deux facteurs pour une sécurité renforcée.
                      </p>
                      <Button
                        className="bg-red-600 hover:bg-red-500 text-white"
                        onClick={handleSetup2FA}
                      >
                        <Fingerprint className="w-4 h-4 mr-2" />
                        Configurer 2FA
                      </Button>
                    </div>
                  ) : twoFASetup.isEnabled ? (
                    <div className="text-center">
                      <CheckCircle className="w-16 h-16 text-emerald-400 mx-auto mb-4" />
                      <p className="text-emerald-400 font-semibold">2FA Activé !</p>
                      <p className="text-slate-400 text-sm mt-2">
                        Votre compte est protégé par l'authentification à deux facteurs.
                      </p>
                    </div>
                  ) : (
                    <div className="space-y-4">
                      <div className="bg-slate-800 p-4 rounded-lg">
                        <p className="text-sm text-slate-300 mb-2">Code secret (pour saisie manuelle) :</p>
                        <code className="text-cyan-400 font-mono text-sm break-all">{twoFASetup.secret}</code>
                      </div>
                      <div className="bg-slate-800 p-4 rounded-lg">
                        <p className="text-sm text-slate-300 mb-2">Ou scannez ce lien dans votre app :</p>
                        <code className="text-cyan-400 font-mono text-xs break-all">{twoFASetup.qrUri}</code>
                      </div>
                      <div>
                        <Label className="text-slate-300">Code de vérification</Label>
                        <Input
                          type="text"
                          value={twoFASetup.verificationCode}
                          onChange={(e) => setTwoFASetup({...twoFASetup, verificationCode: e.target.value})}
                          className="bg-slate-800 border-slate-600 text-white font-mono text-center text-lg"
                          placeholder="000000"
                          maxLength={6}
                        />
                      </div>
                      <Button
                        className="w-full bg-emerald-600 hover:bg-emerald-500 text-white"
                        onClick={handleVerify2FA}
                      >
                        <CheckCircle className="w-4 h-4 mr-2" />
                        Vérifier et Activer
                      </Button>
                    </div>
                  )}
                </div>
              )}
              
              <Button
                variant="outline"
                className="w-full mt-6 border-slate-600 text-slate-300"
                onClick={() => setShowSecurityPanel(false)}
              >
                Fermer
              </Button>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default AdminControlCenter;
